/**
 * Records: the step ledger, filterable, newest first and paginated by cursor.
 * Choosing a row opens that resident's whole timeline.
 */
import React, { useState } from 'react';
import { NativeSelect } from '../bd/bdUi';
import { Domain, KINDS, Kind, StepRow, StepsPage, WindowParams, flowApi } from './flowApi';
import { DOMAINS, DOMAIN_LABEL, ORIGINS, ORIGIN_LABEL } from './chartTheme';
import { DomainName, EmptyNote, OriginPill, Remote, fmtStepWhen, stateLabel, useRemote } from './flowUi';
import TimelineDrawer, { actorLabel, landedWith } from './TimelineDrawer';

export interface PersonFilter { id: string; name: string | null }
/** A facility chosen on the Atlas tab. */
export interface FacilityFilter { id: string; name: string }

export default function RecordsView({
  window: w, origin, onOrigin, person, onPerson, facility = null, onFacility,
}: {
  window: WindowParams;
  /** '' means all origins. */
  origin: string; onOrigin: (o: string) => void;
  person: PersonFilter | null; onPerson: (p: PersonFilter | null) => void;
  facility?: FacilityFilter | null; onFacility?: (f: FacilityFilter | null) => void;
}) {
  const [domain, setDomain] = useState('');
  const [kind, setKind] = useState('');
  // Text filters apply on Apply/Enter, so typing does not fire a request per keystroke.
  const [draft, setDraft] = useState({ userId: person?.id || '', stage: '', patientId: '' });
  const [text, setText] = useState({ stage: '', patientId: '' });
  // One cursor per page already visited; the last entry is the page on screen.
  // A stack is only valid for the filters it was built under: a cursor from one
  // window or filter set means nothing in another, so the stack carries its
  // signature and a mismatch is read as "back to page one".
  const [paging, setPaging] = useState<{ sig: string; stack: Array<string | null> }>({ sig: '', stack: [null] });
  const [open, setOpen] = useState<StepRow | null>(null);

  const userId = person?.id || '';
  const facilityId = facility?.id || '';
  const sig = [w.from, w.to, domain, kind, origin, userId, facilityId, text.stage, text.patientId].join('|');
  const cursors = paging.sig === sig ? paging.stack : [null];
  const cursor = cursors[cursors.length - 1];
  const setCursors = (f: (c: Array<string | null>) => Array<string | null>) => setPaging({ sig, stack: f(cursors) });

  const key = ['steps', sig, cursor || ''].join('|');
  const { state, retry } = useRemote<StepsPage>(key, () => flowApi.steps(w, {
    domain, kind, origin, userId, facilityId, stage: text.stage, patientId: text.patientId, cursor,
  }));

  // Reflect a person chosen on the People tab into the draft input.
  const [seenPerson, setSeenPerson] = useState(person?.id || '');
  if ((person?.id || '') !== seenPerson) { setSeenPerson(person?.id || ''); setDraft((d) => ({ ...d, userId: person?.id || '' })); }

  const apply = (e: React.FormEvent) => {
    e.preventDefault();
    const id = draft.userId.trim();
    if (id !== userId) onPerson(id ? { id, name: null } : null);
    setText({ stage: draft.stage.trim(), patientId: draft.patientId.trim() });
  };

  const filtered = !!(domain || kind || origin || userId || facilityId || text.stage || text.patientId);
  const clearFilters = () => {
    setDomain(''); setKind(''); onOrigin(''); onPerson(null); onFacility?.(null);
    setDraft({ userId: '', stage: '', patientId: '' }); setText({ stage: '', patientId: '' });
  };

  return (
    <div className="space-y-4">
      <form onSubmit={apply} className="tide-panel flex flex-wrap items-end gap-3 p-3" aria-label="Record filters">
        <NativeSelect label="Domain" value={domain} onChange={setDomain}>
          <option value="">All domains</option>
          {DOMAINS.map((d) => <option key={d} value={d}>{DOMAIN_LABEL[d]}</option>)}
        </NativeSelect>
        <NativeSelect label="Kind" value={kind} onChange={setKind}>
          <option value="">All kinds</option>
          {KINDS.map((k: Kind) => <option key={k} value={k}>{k.toLowerCase()}</option>)}
        </NativeSelect>
        <NativeSelect label="Origin" value={origin} onChange={onOrigin}>
          <option value="">All origins</option>
          {ORIGINS.map((o) => <option key={o} value={o}>{ORIGIN_LABEL[o]}</option>)}
        </NativeSelect>
        <label className="tide-field">
          Person (user id)
          <input className="tide-input w-56" value={draft.userId} onChange={(e) => setDraft({ ...draft, userId: e.target.value })} placeholder="actor or owner" />
        </label>
        <label className="tide-field">
          Stage (from or to)
          <input className="tide-input w-40" value={draft.stage} onChange={(e) => setDraft({ ...draft, stage: e.target.value })} />
        </label>
        <label className="tide-field">
          Resident id
          <input className="tide-input w-56" value={draft.patientId} onChange={(e) => setDraft({ ...draft, patientId: e.target.value })} />
        </label>
        <button type="submit" className="tide-btn tide-btn--primary">Apply</button>
      </form>

      {(person || facility) && (
        <p className="flex flex-wrap items-center gap-2 tide-small" style={{ margin: 0 }}>
          {person && (
            <>
              <span className="tide-chip">Person: {person.name || person.id}</span>
              <button type="button" className="tide-link tide-small" onClick={() => onPerson(null)}>Clear person</button>
            </>
          )}
          {facility && (
            <>
              <span className="tide-chip">Facility: {facility.name}</span>
              <button type="button" className="tide-link tide-small" onClick={() => onFacility?.(null)}>Clear facility</button>
            </>
          )}
        </p>
      )}

      <Remote state={state} retry={retry} skeleton="table">
        {(data) => (data.rows.length === 0 && cursors.length === 1 ? (
          <EmptyNote reason={filtered ? 'filtered' : 'none'} onClear={filtered ? clearFilters : undefined}>No steps match these filters in this window.</EmptyNote>
        ) : (
          <section className="tide-panel">
            <div className="tide-scroll" tabIndex={0} role="region" aria-label="Steps (scrolls sideways)">
              <table className="tide-table min-w-[980px]">
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">Domain</th>
                    <th scope="col">Record</th>
                    <th scope="col">Resident</th>
                    <th scope="col">Step</th>
                    <th scope="col">By</th>
                    <th scope="col">Landed with</th>
                    <th scope="col">Origin</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => {
                    const canOpen = !!r.patientId;
                    return (
                      <tr
                        key={r.id}
                        data-testid="step-row"
                        onClick={canOpen ? () => setOpen(r) : undefined}
                        className={canOpen ? 'tide-row-open' : undefined}
                        style={open?.id === r.id ? { background: 'var(--tide-interact-soft)', boxShadow: 'inset 3px 0 0 var(--tide-interact-strong)' } : undefined}
                      >
                        <td className="whitespace-nowrap tide-tiny">{fmtStepWhen(r)}</td>
                        <td><DomainName domain={r.domain as Domain} /></td>
                        <td><div>{r.recordType.replace(/_/g, ' ')}</div><div className="tide-tiny tide-ink-2">{r.kind.toLowerCase()}</div></td>
                        <td>
                          {canOpen ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setOpen(r); }}
                              className="tide-link"
                              aria-label={`Open timeline for ${r.residentLabel || 'resident'}`}
                            >{r.residentLabel || 'Resident'}</button>
                          ) : <span className="tide-ink-2">no resident</span>}
                          {r.facilityName && <div className="tide-tiny tide-ink-2">{r.facilityName}</div>}
                        </td>
                        <td>{stateLabel(r.fromState)} <span aria-hidden>&rarr;</span><span className="tide-sr">to</span> {stateLabel(r.toState)}</td>
                        <td>{actorLabel(r)}</td>
                        <td>{landedWith(r)}</td>
                        <td><OriginPill origin={r.origin} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 tide-small tide-ink-2" style={{ borderTop: '1px solid var(--tide-line)' }}>
              <span>Page {cursors.length} · {data.rows.length} {data.rows.length === 1 ? 'step' : 'steps'}</span>
              <span className="flex gap-2">
                <button type="button" disabled={cursors.length === 1} onClick={() => setCursors((c) => c.slice(0, -1))} className="tide-btn">Newer</button>
                <button type="button" disabled={!data.nextCursor} onClick={() => setCursors((c) => [...c, data.nextCursor])} className="tide-btn">Older</button>
              </span>
            </div>
          </section>
        ))}
      </Remote>

      <TimelineDrawer patientId={open?.patientId || null} label={open?.residentLabel} onClose={() => setOpen(null)} />
    </div>
  );
}
